/**
 * Páginas de sistema del OS (brand book SOA 10 PRO · §14). Todas usan la plantilla PaginaSistema.
 * Cada una documenta «cuándo aparece».
 */
import { useState } from "react";
import PaginaSistema from "./PaginaSistema.jsx";

const recargar = () => window.location.reload();
const abrirBuscador = () => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true, ctrlKey: true, bubbles: true }));

/** Cuándo: el backend responde 503 con codigo "mantenimiento" (candado de acceso). Oscura. */
export function PaginaMantenimiento({ vuelve = "23:30" }) {
  return (
    <PaginaSistema
      oscuro tono="ambar"
      titulo="Estamos instalando SOA 10 PRO"
      texto="El OS está cerrado mientras probamos todo en producción. Lo que guardaste antes de las 22:00 está a salvo."
      chip={`Volvemos hoy, antes de las ${vuelve}`}
      primario={{ label: "Reintentar", onClick: recargar }}
      pie="Se reabre sola cuando terminemos (reintenta cada 60 segundos). ¿Algo urgente de un cliente? Escribile a tu EC por Chat."
    />
  );
}

/** Cuándo: error de render no controlado (500/502/504 de la app). `referencia` = id de Sentry si existe. */
export function PaginaError({ referencia, detalle, onReintentar = recargar }) {
  const [ver, setVer] = useState(false);
  return (
    <PaginaSistema
      tono="gris"
      titulo="Algo no anduvo"
      texto="Ya avisamos al equipo. Probá de nuevo; si sigue igual, pasale este código a soporte."
      chip={`ref ${referencia || "sin-código"}`}
      primario={{ label: "Reintentar", onClick: onReintentar }}
      secundario={{ label: "Ir al inicio", href: "/" }}
    >
      {detalle && (
        <div style={{ marginTop: 16 }}>
          <button type="button" onClick={() => setVer(v => !v)} style={{ background: "none", border: 0, color: "#4B4F59", fontSize: 13, textDecoration: "underline", cursor: "pointer", padding: 0 }}>
            {ver ? "Ocultar detalle técnico" : "Ver detalle técnico"}
          </button>
          {ver && <pre style={{ marginTop: 8, padding: 12, background: "#111318", color: "#E7E9EE", borderRadius: 12, fontSize: 12, maxHeight: 220, overflow: "auto", whiteSpace: "pre-wrap" }}>{detalle}</pre>}
        </div>
      )}
    </PaginaSistema>
  );
}

/** Cuándo: la ruta no existe y no está en las redirecciones vigentes. */
export function Pagina404({ ruta }) {
  return (
    <PaginaSistema
      tono="violeta" encendido={1}
      titulo="No encontramos esa página"
      texto="El enlace puede estar viejo o escrito distinto. Probá buscarla."
      chip={ruta || window.location.pathname}
      primario={{ label: "Buscar con ⌘K", onClick: abrirBuscador }}
      secundario={{ label: "Ir al inicio", href: "/" }}
    />
  );
}

/** Cuándo: el rol del usuario no tiene acceso a la sección (403). */
export function Pagina403({ rol }) {
  return (
    <PaginaSistema
      tono="violeta" encendido={2}
      titulo="Esta sección no es para tu rol"
      texto="Si la necesitás para trabajar, pedile acceso a quien administra los roles."
      chip={rol ? `tu rol: ${rol}` : null}
      primario={{ label: "Volver al inicio", href: "/" }}
    />
  );
}

/** Cuándo: el navegador no tiene conexión y la app no puede cargar datos. */
export function PaginaSinConexion() {
  return (
    <PaginaSistema
      tono="gris" encendido={3}
      titulo="Sin conexión"
      texto="Cuando vuelva la señal seguimos. Lo que ya estaba guardado no se pierde."
      primario={{ label: "Reintentar", onClick: recargar }}
    />
  );
}

/** Cuándo: la sesión venció (401 que no se recuperó con el refresh del token). */
export function PaginaSesionVencida({ onEntrar }) {
  return (
    <PaginaSistema
      tono="verde"
      titulo="Tu sesión venció"
      texto="Entrá de nuevo y volvés exactamente a donde estabas."
      primario={{ label: "Entrar de nuevo", onClick: onEntrar || recargar }}
    />
  );
}

/** Cuándo: entró con una cuenta que no es @thecrewagencia.com. */
export function PaginaCuentaAjena({ email, onOtraCuenta }) {
  return (
    <PaginaSistema
      tono="violeta" encendido={3}
      titulo="Entraste con otra cuenta"
      texto="SOA 10 PRO es solo para cuentas @thecrewagencia.com. Si sos cliente, tu lugar es el portal."
      chip={email}
      primario={{ label: "Probar con otra cuenta", onClick: onOtraCuenta || recargar }}
      secundario={{ label: "Ir al portal de clientes", href: "https://clientes.thecrewagencia.com" }}
    />
  );
}
